import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../Icons';
import styles from './Pager.module.css';

/**
 * Paginador compartido.
 *
 * ## Por qué existe
 *
 * Había **tres** paginadores distintos: el de Loads (ventana de números, sin totales), el de
 * Catálogos (dos flechas `‹ ›` y una línea de texto) y el de la tabla de embarques. El de Catálogos
 * era el que el usuario describió como "muy simple y poco visible", y con razón: dos caracteres de
 * 12px sin fondo ni borde.
 *
 * 📌 En lugar de maquillar ese, se unifican. Tres implementaciones de lo mismo garantizan que la
 * siguiente mejora se aplique a una sola y las otras se queden atrás — que es exactamente cómo
 * llegaron a verse distintas.
 *
 * ## Qué muestra, y por qué cada cosa
 *
 * - **El rango y el total** (`1–25 of 226`). Con paginación del lado del servidor el navegador solo
 *   tiene una página, así que sin este dato no hay forma de saber cuántos registros hay ni en qué
 *   parte del listado estás.
 * - **Primera y última** además de anterior/siguiente: sin ellas, llegar al final de 40 páginas son
 *   39 clics.
 * - **Ventana de números** para saltar de cerca, que es el caso real — nadie salta a la página 27.
 */

interface PagerProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Total de registros. Si se omite, no se muestra el rango. */
  total?: number;
  /** Tamaño de página, para calcular el rango mostrado. */
  pageSize?: number;
  /** Nombre de lo que se lista, para el texto del rango. */
  itemLabel?: string;
  /**
   * Plural, cuando no es `itemLabel + 's'`.
   *
   * ⚠️ Hace falta: agregar la `s` a "delivery" da **"deliverys"**. El plural del inglés no se puede
   * derivar con una regla de una línea, así que el caso irregular se pasa explícito en lugar de
   * intentar adivinarlo aquí.
   */
  itemLabelPlural?: string;
  /** Opciones de tamaño de página. Si se omite, no se muestra el selector. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

/**
 * Ventana de números a mostrar.
 *
 * 📌 Se centra en la página actual pero se **ancla** en los extremos: sin el anclaje, en la página 1
 * la ventana saldría con números negativos y en la última quedaría a la mitad del ancho, así que la
 * barra cambiaría de tamaño al navegar.
 */
const windowOf = (page: number, total: number, max = 5): number[] => {
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);

  let start = Math.max(1, page - Math.floor(max / 2));
  const end = Math.min(total, start + max - 1);
  start = Math.max(1, end - max + 1);

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const Pager: React.FC<PagerProps> = ({
  page,
  totalPages,
  onChange,
  total,
  pageSize,
  itemLabel = 'record',
  itemLabelPlural,
  pageSizeOptions,
  onPageSizeChange,
}) => {
  const [sizeMenuOpen, setSizeMenuOpen] = React.useState(false);
  const sizeRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!sizeMenuOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (sizeRef.current && !sizeRef.current.contains(e.target as Node)) setSizeMenuOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [sizeMenuOpen]);

  /**
   * 📌 **No se oculta con una sola página.** El paginador de Loads devolvía `null` en ese caso, y con
   * paginación del servidor eso se lee como que el listado no tiene totales: desaparece justo el
   * texto que dice "23 records". Con una página se muestra el rango y las flechas quedan inertes.
   */
  const pages = windowOf(page, Math.max(1, totalPages));
  const showRange = typeof total === 'number' && typeof pageSize === 'number' && total > 0;
  const from = showRange ? (page - 1) * (pageSize as number) + 1 : 0;
  const to = showRange ? Math.min(total as number, page * (pageSize as number)) : 0;

  const go = (target: number) => {
    const next = Math.min(Math.max(1, target), Math.max(1, totalPages));
    if (next !== page) onChange(next);
  };

  return (
    <div className={styles.wrap}>
      {showRange && (
        <span className={styles.range}>
          <strong>
            {from.toLocaleString()}–{to.toLocaleString()}
          </strong>{' '}
          of {(total as number).toLocaleString()}{' '}
          {total === 1 ? itemLabel : itemLabelPlural || `${itemLabel}s`}
        </span>
      )}

      <div className={styles.controls}>
        {/* Doble galón: primera y última. Se ocultan con pocas páginas, donde no aportan. */}
        {totalPages > 5 && (
          <button
            type="button"
            className={styles.btn}
            onClick={() => go(1)}
            disabled={page <= 1}
            aria-label="First page"
          >
            <span className={styles.double}>
              <ChevronLeftIcon size={13} />
              <ChevronLeftIcon size={13} />
            </span>
          </button>
        )}

        <button
          type="button"
          className={styles.btn}
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={15} />
        </button>

        {pages.map((p) => (
          <button
            key={p}
            type="button"
            className={[styles.btn, styles.num, p === page ? styles.active : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => go(p)}
            aria-current={p === page ? 'page' : undefined}
            aria-label={`Page ${p}`}
          >
            {p}
          </button>
        ))}

        <button
          type="button"
          className={styles.btn}
          onClick={() => go(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRightIcon size={15} />
        </button>

        {totalPages > 5 && (
          <button
            type="button"
            className={styles.btn}
            onClick={() => go(totalPages)}
            disabled={page >= totalPages}
            aria-label="Last page"
          >
            <span className={styles.double}>
              <ChevronRightIcon size={13} />
              <ChevronRightIcon size={13} />
            </span>
          </button>
        )}
      </div>

      {pageSizeOptions && onPageSizeChange && (
        <div className={styles.size} ref={sizeRef}>
          <button
            type="button"
            className={styles.sizeBtn}
            onClick={() => setSizeMenuOpen((o) => !o)}
            aria-expanded={sizeMenuOpen}
          >
            {pageSize} / page
          </button>
          {sizeMenuOpen && (
            <div className={styles.sizeMenu}>
              {pageSizeOptions.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={[styles.sizeOption, size === pageSize ? styles.sizeOptionActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    onPageSizeChange(size);
                    setSizeMenuOpen(false);
                  }}
                >
                  {size} / page
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Pager;
